import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { OrderReason } from '@/shared/components/order-reason';
import { SortableOrderList } from '@/shared/components/sortable-order-list';
import { StatusBadge } from '@/shared/components/status-badge';

import { useCategoryOrder, useSaveCategoryOrder } from '../api/categories.queries';
import type { Category } from '../types/category.types';

/**
 * The order of the category tabs on the storefront, in every country
 * (catalogue.manage, D-27). The same editor as the brand order: drag a row by
 * its handle (or with the keyboard), or use its menu to move it to the top, the
 * bottom or a given position; give a reason; save.
 *
 * The list is shown exactly as the backend returns it. Until an order is saved
 * the storefront keeps its current order; once saved, a category created later
 * is shown after the ordered ones. Mount only while open.
 */
export function CategoryOrderDialog({ onClose }: { onClose: () => void }) {
  const current = useCategoryOrder(true);
  // Owned here, not by the editor, so a refusal stays on screen after the
  // list is reloaded and the editor starts afresh.
  const save = useSaveCategoryOrder();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle className="text-base">Storefront order</DialogTitle>
            <HistoryButton
              entityType="CategoryOrder"
              entityId="storefront"
              name="the storefront order"
            />
          </div>
          <DialogDescription className="text-xs">
            The order of the category tabs on the storefront, in every country. Brands are ordered
            separately, in Storefront brand order.
          </DialogDescription>
        </DialogHeader>
        {current.isLoading ? (
          <Skeleton className="h-48" />
        ) : current.error || !current.data ? (
          <ErrorState error={current.error} onRetry={() => void current.refetch()} />
        ) : (
          <OrderEditor
            // A refetch (e.g. after a refused save) starts the editor afresh.
            key={current.dataUpdatedAt}
            initial={current.data}
            save={save}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderEditor({
  initial,
  save,
  onClose,
}: {
  initial: Category[];
  save: ReturnType<typeof useSaveCategoryOrder>;
  onClose: () => void;
}) {
  const [order, setOrder] = useState(initial);
  const [reason, setReason] = useState('');
  const saved = initial.every((c) => c.sortOrder != null);
  const originalIndex = new Map(initial.map((category, index) => [category.id, index]));
  const moved = order.filter((c, index) => originalIndex.get(c.id) !== index).length;
  const name = (category: Category) => category.nameEn ?? `Category ${category.id}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-xs">
      {!saved && (
        <p className="text-muted-foreground">
          No order has been saved yet: the storefront shows categories in the order the database
          returns them. Saving pins this order.
        </p>
      )}
      <SortableOrderList
        className="min-h-0 flex-1"
        listId="category-order"
        label="Category order"
        items={order}
        onChange={setOrder}
        originalIndex={originalIndex}
        itemLabel={name}
        disabled={save.isPending}
        renderItem={(category) => (
          <>
            <span className="truncate font-medium">{name(category)}</span>
            {!category.isActive && <StatusBadge variant="neutral">Inactive</StatusBadge>}
          </>
        )}
      />
      <OrderReason id="category-order-reason" value={reason} onChange={setReason} />
      {save.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-muted-foreground me-auto" aria-live="polite">
          {moved > 0
            ? `${moved} categor${moved === 1 ? 'y' : 'ies'} moved.`
            : saved
              ? 'No changes yet.'
              : 'Not saved yet.'}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          // While no order is saved, saving the shown order pins it.
          disabled={(saved && moved === 0) || reason.trim().length < 3 || save.isPending}
          onClick={() =>
            save.mutate(
              { categoryIds: order.map((c) => c.id), reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Save order
        </Button>
      </div>
    </div>
  );
}
